import { Code, ConnectError } from '@connectrpc/connect'

interface StructuredErrorJson {
  coinError?: Record<string, unknown>
  nsfwError?: Record<string, unknown>
  validationError?: {
    violations?: Array<{ field?: string; reason?: string; description?: string }>
  }
}

export interface MessageFailureLike {
  code?: string | number
  message?: string
  error?: StructuredErrorJson
}

function isSupersededFailure(failure: MessageFailureLike): boolean {
  const code = failure.code
  return code === 'MESSAGE_FAILURE_CODE_SUPERSEDED' || code === 1
}

function connectErrorWithDebug(
  message: string,
  code: Code,
  debug: Record<string, unknown>,
): ConnectError {
  const error = new ConnectError(message, code)
  ;(error as unknown as { details: unknown[] }).details = [{ debug }]
  return error
}

function connectErrorFromStructuredErrorJson(
  error: StructuredErrorJson,
  message: string,
): ConnectError {
  if (error.coinError) {
    return connectErrorWithDebug(message, Code.FailedPrecondition, {
      type: 'ERROR_TYPE_COIN',
      coinError: error.coinError,
    })
  }
  if (error.nsfwError) {
    return connectErrorWithDebug(message, Code.InvalidArgument, { type: 'ERROR_TYPE_NSFW' })
  }
  if (error.validationError?.violations?.length) {
    return connectErrorWithDebug(message, Code.InvalidArgument, {
      fieldViolations: error.validationError.violations.map(violation => ({
        field: violation.field,
        reason: violation.reason,
        description: violation.description,
      })),
    })
  }
  return new ConnectError(message, Code.Unknown)
}

export function connectErrorFromMessageFailure(failure: MessageFailureLike): ConnectError | null {
  if (isSupersededFailure(failure)) {
    return null
  }
  const message = failure.message || 'Something went wrong.'
  if (failure.error) {
    return connectErrorFromStructuredErrorJson(failure.error, message)
  }
  return new ConnectError(message, Code.Unknown)
}

export function shouldSurfaceMessageFailure(
  failure: MessageFailureLike | undefined,
): failure is MessageFailureLike {
  return Boolean(failure && !isSupersededFailure(failure))
}

interface AgentFailureCarrier {
  turnCompleted?: { status?: string; messageId?: string; failure?: MessageFailureLike }
  generationFailed?: { failure?: MessageFailureLike; messageId?: string }
}

export function agentNotificationFailures(agent: unknown) {
  const payload = agent as AgentFailureCarrier
  const failures: MessageFailureLike[] = []
  if (payload.turnCompleted?.status === 'MESSAGE_STATUS_FAILED' && payload.turnCompleted.failure) {
    failures.push(payload.turnCompleted.failure)
  }
  if (payload.generationFailed?.failure) {
    failures.push(payload.generationFailed.failure)
  }
  return failures
}
